import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { getDefaultStore, useAtomValue, useSetAtom } from "jotai";
import { AnimatePresence, motion } from "motion/react";
import { type JSX, useMemo, useState } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import {
  type BatchAdjustAvailabilityQueryQuery,
  CalendarPageAppointmentsQueryDocument,
  CalendarPageFreeSlotsQueryDocument,
  WaitingRoomPageQueryDocument,
} from "@repo/graphql-types/graphql";
import SwitchHorizontal from "@repo/icons/default/SwitchHorizontal";
import Warning from "@repo/icons/default/Warning";
import { CpsAlert } from "corpus";

import { BatchClosureCard } from "@/components/batch-adjust/batch-closure-card";
import { BatchNotifyAllHeader } from "@/components/batch-adjust/batch-notify-all-header";
import { BatchNotifyPatientCheckbox } from "@/components/batch-adjust/batch-notify-patient-checkbox";
import { BatchPatientName } from "@/components/batch-adjust/batch-patient-name";
import { BatchRecurringNotice } from "@/components/batch-adjust/batch-recurring-notice";
import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import { Button } from "@/components/ui/button/button";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { Skeleton } from "@/components/ui/skeleton/skeleton";
import {
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import {
  type BatchAdjustTarget,
  batchAdjustAppliedIdsAtom,
  batchAdjustOriginAtom,
  batchAdjustTargetsAtom,
} from "@/lib/atoms/batch-adjust-form-atom";
import type { MotivoIndisponibilidade } from "@/lib/atoms/recurring-appointment-form-atom";
import { UnavailableSlotType } from "@/lib/enums/unavailable-slot-type-enum";
import {
  addMinutesToTime,
  directionLabel,
  directionToDelta,
  FECHAMENTO_TIPO_LABEL,
  FECHAMENTOS_IGNORAVEIS,
  MOTIVO_INDISPONIBILIDADE_POR_CODIGO,
  timesOverlap,
  toDisplayTime,
} from "@/lib/helpers/batch-adjust";
import { trackBatchAppointmentsChanged } from "@/lib/helpers/batch-adjust-tracking";
import { getUnavailabilityLabel } from "@/lib/helpers/recurrence";

const searchSchema = z.object({
  direction: z.enum(["advance", "delay"]),
  minutes: z.coerce.number(),
});

const MOTIVO_FECHAMENTO = 4;

const BatchAdjustAvailabilityQuery = graphql(/* GraphQL */ `
  query BatchAdjustAvailabilityQuery(
    $agendamentos: [ReagendamentoEmBatchItemInput!]!
    $ignorarFechamento: Boolean
  ) {
    horarios: buscarReagendamentoEmBatch(
      agendamentos: $agendamentos
      ignorarFechamento: $ignorarFechamento
    ) {
      codAgendamento
      autorizado
      motivoIndisponibilidade
    }
  }
`);

const BatchAdjustMutation = graphql(/* GraphQL */ `
  mutation BatchAdjustMutation($input: RemarcarAgendamentosEmBatchInput!) {
    remarcarAgendamentosEmBatch(input: $input) {
      data: remarcarEmBatchOutput {
        sucessos {
          codAgendamentoNovo
          codAgendamentoOrigem
        }
      }
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

const BatchAdjustClosuresQuery = graphql(/* GraphQL */ `
  query BatchAdjustClosuresQuery($dataInicio: date!, $dataFim: date!) {
    tbFechamentos(
      where: { _and: [{ data: { _gte: $dataInicio } }, { data: { _lte: $dataFim } }] }
    ) {
      codFechamento
      codUnidade
      data
      horaInicio
      horaFim
      motivo
      codTipoFechamento
    }
  }
`);

interface ClosureInfo {
  codFechamento: number;
  horaInicio: string;
  horaFim: string;
  motivo: string;
  tipoLabel: string;
  ignoravel: boolean;
  deslocamento: boolean;
}

interface ItemAvailability {
  loading: boolean;
  disponivel: boolean;
  motivo?: MotivoIndisponibilidade | null;
}

interface BatchAdjustReviewItemProps {
  target: BatchAdjustTarget;
  newStart: string;
  newEnd: string;
  loading: boolean;
  expectNotify: boolean;
  availability?: ItemAvailability;
  unavailableLabel: string;
  willAdjust: boolean;
  notify: boolean;
  onNotifyChange: (checked: boolean) => void;
}

const BatchAdjustReviewItem = ({
  target,
  newStart,
  newEnd,
  loading,
  expectNotify,
  availability,
  unavailableLabel,
  willAdjust,
  notify,
  onNotifyChange,
}: BatchAdjustReviewItemProps): JSX.Element => {
  const disponivel = availability?.disponivel ?? false;
  const unavailable = !loading && !disponivel;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <BatchPatientName
            nomePaciente={target.nomePaciente}
            recorrente={target.recorrente}
          />
          <span className="flex items-center gap-2 text-neutral-500 text-xs">
            <span className="text-neutral-400 line-through">
              {toDisplayTime(target.horaInicio)}
            </span>
            <SwitchHorizontal size={12} className="shrink-0 fill-neutral-300" />
            {loading ? (
              <Skeleton
                className="h-3 w-20 rounded-sm"
                role="status"
                aria-label="Verificando disponibilidade"
                aria-busy="true"
              />
            ) : (
              <span
                className={
                  willAdjust
                    ? "font-medium text-secondary-500"
                    : "text-neutral-400 line-through"
                }
              >
                {toDisplayTime(newStart)} - {toDisplayTime(newEnd)}
              </span>
            )}
          </span>
        </div>
        {loading && expectNotify ? (
          <Skeleton className="size-6 shrink-0 rounded-md" />
        ) : null}
        <AnimatePresence initial={false}>
          {willAdjust && !loading ? (
            <motion.span
              key="notify"
              className="flex shrink-0"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
            >
              <BatchNotifyPatientCheckbox
                codAgendamento={target.codAgendamento}
                nomePaciente={target.nomePaciente}
                checked={notify}
                onCheckedChange={onNotifyChange}
              />
            </motion.span>
          ) : null}
        </AnimatePresence>
      </div>

      {unavailable && unavailableLabel ? (
        <div className="flex items-center gap-2 rounded-lg bg-neutral-50 p-3 text-neutral-600 text-xs">
          <Warning size={16} className="shrink-0 fill-warning-500" />
          {unavailableLabel}
        </div>
      ) : null}

      {target.recorrente ? (
        <BatchRecurringNotice
          data={target.data}
          horaInicio={target.horaInicio}
          horaFim={target.horaFim}
          action="adjust"
        />
      ) : null}
    </div>
  );
};

const closurePeriod = (closure: ClosureInfo): string =>
  `${toDisplayTime(closure.horaInicio)} às ${toDisplayTime(closure.horaFim)}`;

const uniqueClosures = (closures: ClosureInfo[]): ClosureInfo[] => [
  ...new Map(closures.map((closure) => [closure.codFechamento, closure])).values(),
];

const fechamentoDescriptionFor = (closures: ClosureInfo[]): string =>
  closures
    .map((closure) =>
      closures.length > 1
        ? `${closurePeriod(closure)} · ${closure.tipoLabel} · ${closure.motivo}`
        : `${closure.tipoLabel} · ${closure.motivo}`,
    )
    .join(" · ");

const fechamentoTitleFor = (closures: ClosureInfo[]): string =>
  closures.length === 1
    ? `Fechamento das ${closurePeriod(closures[0])}`
    : "Fechamentos no novo horário";

type AvailabilitySlots = BatchAdjustAvailabilityQueryQuery["horarios"];

const toAvailabilityMap = (
  slots: AvailabilitySlots | undefined,
): Record<number, ItemAvailability> => {
  const map: Record<number, ItemAvailability> = {};
  for (const slot of slots ?? []) {
    if (!slot) continue;
    const motivoInt = slot.motivoIndisponibilidade ?? null;
    map[slot.codAgendamento] = {
      loading: false,
      disponivel: slot.autorizado,
      motivo:
        motivoInt != null
          ? (MOTIVO_INDISPONIBILIDADE_POR_CODIGO[motivoInt] ?? null)
          : null,
    };
  }
  return map;
};

const confirmLabelFor = (count: number, isPartial: boolean): string => {
  if (!isPartial) return "Confirmar ajuste";
  return count === 1 ? "Ajustar 1 agendamento" : `Ajustar ${count} agendamentos`;
};

const partialDescriptionFor = (adjusted: number, total: number): string =>
  `${adjusted} de ${total} agendamentos ${adjusted === 1 ? "será ajustado" : "serão ajustados"}. Os que não têm disponibilidade continuam no horário atual.`;

const BatchAdjustReviewPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { direction, minutes } = useSearch({
    from: "/calendar/batch-adjust/review",
  });
  const targets = useAtomValue(batchAdjustTargetsAtom);
  const origin = useAtomValue(batchAdjustOriginAtom);

  const delta = directionToDelta(direction, minutes);
  const label = directionLabel(direction);

  const batchItems = useMemo(
    () =>
      targets.map((target) => ({
        codAgendamento: target.codAgendamento,
        horario: addMinutesToTime(target.horaInicio, delta),
      })),
    [targets, delta],
  );

  const [ignoreAllClosures, setIgnoreAllClosures] = useState(false);

  const availabilityResult = useGraphQL(
    BatchAdjustAvailabilityQuery,
    { agendamentos: batchItems, ignorarFechamento: false },
    { staleTime: 0, gcTime: 0, enabled: targets.length > 0 },
  );

  const ignoringClosuresResult = useGraphQL(
    BatchAdjustAvailabilityQuery,
    { agendamentos: batchItems, ignorarFechamento: true },
    {
      staleTime: 0,
      gcTime: 0,
      enabled:
        targets.length > 0 &&
        (availabilityResult.data?.horarios ?? []).some(
          (slot) => slot?.motivoIndisponibilidade === MOTIVO_FECHAMENTO,
        ),
    },
  );

  const availabilityByTarget = useMemo(
    () => toAvailabilityMap(availabilityResult.data?.horarios),
    [availabilityResult.data],
  );

  const ignoringClosuresByTarget = useMemo(
    () => toAvailabilityMap(ignoringClosuresResult.data?.horarios),
    [ignoringClosuresResult.data],
  );

  const closuresResult = useGraphQL(
    BatchAdjustClosuresQuery,
    { dataInicio: targets[0]?.data ?? "", dataFim: targets[0]?.data ?? "" },
    { staleTime: 0, gcTime: 0, enabled: targets.length > 0 },
  );

  const closureByTarget = useMemo(() => {
    const closures = closuresResult.data?.tbFechamentos ?? [];
    const map: Record<number, ClosureInfo> = {};

    for (const target of targets) {
      const newStart = addMinutesToTime(target.horaInicio, delta);
      const newEnd = addMinutesToTime(target.horaFim, delta);

      const overlapping = closures.filter(
        (fechamento) =>
          fechamento.data === target.data &&
          (fechamento.codUnidade == null ||
            fechamento.codUnidade === target.codUnidade) &&
          timesOverlap(newStart, newEnd, fechamento.horaInicio, fechamento.horaFim),
      );

      if (overlapping.length === 0) continue;

      const naoIgnoravel = overlapping.find(
        (fechamento) => !FECHAMENTOS_IGNORAVEIS.has(fechamento.codTipoFechamento),
      );
      const primary = naoIgnoravel ?? overlapping[0];

      map[target.codAgendamento] = {
        codFechamento: primary.codFechamento,
        horaInicio: primary.horaInicio,
        horaFim: primary.horaFim,
        motivo: primary.motivo ?? "Fechamento",
        tipoLabel: FECHAMENTO_TIPO_LABEL[primary.codTipoFechamento] ?? "Fechamento",
        ignoravel: naoIgnoravel === undefined,
        deslocamento: primary.codTipoFechamento === UnavailableSlotType.CreatedByMovement,
      };
    }

    return map;
  }, [closuresResult.data, targets, delta]);

  const fechamentoTargets = targets.filter(
    (target) => availabilityByTarget[target.codAgendamento]?.motivo === "FECHAMENTO",
  );
  const hasFechamento = fechamentoTargets.length > 0;
  const allFechamentosIgnoraveis =
    hasFechamento &&
    fechamentoTargets.every(
      (target) => closureByTarget[target.codAgendamento]?.ignoravel === true,
    );

  const fechamentosDoLote = uniqueClosures(
    fechamentoTargets
      .map((target) => closureByTarget[target.codAgendamento])
      .filter((closure): closure is ClosureInfo => closure !== undefined),
  );

  const isIgnoringClosures = ignoreAllClosures && allFechamentosIgnoraveis;
  const effectiveAvailabilityByTarget = isIgnoringClosures
    ? ignoringClosuresByTarget
    : availabilityByTarget;

  const isResolved = (target: BatchAdjustTarget): boolean =>
    effectiveAvailabilityByTarget[target.codAgendamento]?.disponivel === true;

  const anyLoading =
    availabilityResult.isPending ||
    closuresResult.isPending ||
    (isIgnoringClosures && ignoringClosuresResult.isPending);
  const resolvedTargets = targets.filter(isResolved);

  const unavailableLabelFor = (target: BatchAdjustTarget): string => {
    const availability = effectiveAvailabilityByTarget[target.codAgendamento];
    if (!availability || availability.disponivel) return "";

    if (availability.motivo === "FECHAMENTO") {
      const closure = closureByTarget[target.codAgendamento];
      return closure
        ? `Cruza o fechamento das ${closurePeriod(closure)}`
        : getUnavailabilityLabel("FECHAMENTO");
    }

    if (availability.motivo === "AGENDAMENTO_CONFLITANTE") {
      const newStart = addMinutesToTime(target.horaInicio, delta);
      const newEnd = addMinutesToTime(target.horaFim, delta);
      const blocker = targets.find(
        (other) =>
          other.codAgendamento !== target.codAgendamento &&
          !isResolved(other) &&
          timesOverlap(newStart, newEnd, other.horaInicio, other.horaFim),
      );
      if (blocker) {
        return `Conflita com ${blocker.nomePaciente} às ${toDisplayTime(blocker.horaInicio)}, que continua no horário`;
      }
    }

    return getUnavailabilityLabel(availability.motivo ?? undefined);
  };
  const unresolvedCount = targets.length - resolvedTargets.length;
  const isPartial = !anyLoading && resolvedTargets.length > 0 && unresolvedCount > 0;

  const predictableClosureOf = (target: BatchAdjustTarget): ClosureInfo | undefined => {
    const closure = closureByTarget[target.codAgendamento];
    return closure && !closure.deslocamento ? closure : undefined;
  };
  const predictedClosures = uniqueClosures(
    targets
      .map(predictableClosureOf)
      .filter((closure): closure is ClosureInfo => closure !== undefined),
  );
  const isPredictingClosures =
    availabilityResult.isPending &&
    !closuresResult.isPending &&
    predictedClosures.length > 0;
  const shownClosures = hasFechamento ? fechamentosDoLote : predictedClosures;
  const shownClosuresIgnoraveis = hasFechamento
    ? allFechamentosIgnoraveis
    : predictedClosures.every((closure) => closure.ignoravel);
  const expectsNotify = (target: BatchAdjustTarget): boolean =>
    !(isPredictingClosures && predictableClosureOf(target) !== undefined);
  const showNotifyHeader =
    resolvedTargets.length > 0 || (anyLoading && targets.some(expectsNotify));

  const [notifyIds, setNotifyIds] = useState<number[]>([]);
  const setNotify = (codAgendamento: number, checked: boolean): void => {
    setNotifyIds((prev) =>
      checked ? [...prev, codAgendamento] : prev.filter((id) => id !== codAgendamento),
    );
  };
  const notifiedCount = resolvedTargets.filter((target) =>
    notifyIds.includes(target.codAgendamento),
  ).length;
  const setNotifyAll = (checked: boolean): void => {
    setNotifyIds(checked ? resolvedTargets.map((target) => target.codAgendamento) : []);
  };
  const setAppliedIds = useSetAtom(batchAdjustAppliedIdsAtom);

  const { mutate, isPending } = useGraphQLMutationWithErrorHandler(BatchAdjustMutation, {
    retry: 0,
  });
  const invalidateCalendarAppointments = useInvalidateQuery(
    CalendarPageAppointmentsQueryDocument,
  );
  const invalidateCalendarFreeSlots = useInvalidateQuery(
    CalendarPageFreeSlotsQueryDocument,
  );
  const invalidateWaitingRoom = useInvalidateQuery(WaitingRoomPageQueryDocument);

  const canConfirm = !anyLoading && !isPending && resolvedTargets.length > 0;

  const handleConfirm = (): void => {
    const agendamentos = resolvedTargets.map((target) => ({
      codAgendamento: target.codAgendamento,
      data: target.data,
      horaInicio: addMinutesToTime(target.horaInicio, delta),
      horaFim: addMinutesToTime(target.horaFim, delta),
      ignorarEnvioEmail: !notifyIds.includes(target.codAgendamento),
    }));

    mutate(
      { input: { agendamentos, ignorarFechamento: isIgnoringClosures } },
      {
        onSuccess: () => {
          invalidateCalendarAppointments();
          invalidateCalendarFreeSlots();
          invalidateWaitingRoom();
          setAppliedIds(resolvedTargets.map((target) => target.codAgendamento));
          trackBatchAppointmentsChanged({
            codAgendamentos: resolvedTargets.map((target) => target.codAgendamento),
            totalSelecionado: targets.length,
            acao: direction === "advance" ? "adiantado" : "atrasado",
            minutos: minutes,
            origin,
          });
          void navigate({
            to: "/calendar/batch-adjust/success",
            search: {
              action: "adjust",
              direction,
              minutes,
              count: resolvedTargets.length,
            },
          });
        },
      },
    );
  };

  const confirmLabel = confirmLabelFor(resolvedTargets.length, isPartial);

  return (
    <RoundedLayout className="-mb-[env(safe-area-inset-bottom)]">
      <RecurrenceCreateHeader
        title="Revisar ajuste"
        subtitle={`${label} ${minutes} min`}
      />
      <Page className="flex flex-col">
        <div className="flex h-full flex-col justify-between gap-4">
          <div className="flex flex-col gap-4">
            <AnimatePresence initial={false}>
              {hasFechamento || isPredictingClosures ? (
                <motion.div
                  key="closure"
                  className="overflow-hidden"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <BatchClosureCard
                    title={fechamentoTitleFor(shownClosures)}
                    description={fechamentoDescriptionFor(shownClosures)}
                    ignorable={shownClosuresIgnoraveis}
                    ignoring={ignoreAllClosures}
                    disabled={!hasFechamento}
                    onIgnoringChange={setIgnoreAllClosures}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>

            <div className="rounded-lg border-[1.5px] border-neutral-100 p-4">
              <AnimatePresence initial={false}>
                {showNotifyHeader ? (
                  <motion.div
                    key="notify-header"
                    className="overflow-hidden"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <div className="mb-2.5 border-neutral-100 border-b pb-2.5">
                      <BatchNotifyAllHeader
                        loading={anyLoading}
                        selectedCount={notifiedCount}
                        total={resolvedTargets.length}
                        onCheckedChange={setNotifyAll}
                      />
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
              <div className="flex flex-col divide-y divide-neutral-100 *:py-2.5 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
                {targets.map((target) => (
                  <BatchAdjustReviewItem
                    key={target.codAgendamento}
                    target={target}
                    newStart={addMinutesToTime(target.horaInicio, delta)}
                    newEnd={addMinutesToTime(target.horaFim, delta)}
                    loading={anyLoading}
                    expectNotify={expectsNotify(target)}
                    availability={effectiveAvailabilityByTarget[target.codAgendamento]}
                    unavailableLabel={unavailableLabelFor(target)}
                    willAdjust={isResolved(target)}
                    notify={notifyIds.includes(target.codAgendamento)}
                    onNotifyChange={(checked) =>
                      setNotify(target.codAgendamento, checked)
                    }
                  />
                ))}
              </div>
            </div>

            <AnimatePresence initial={false}>
              {isPartial ? (
                <motion.div
                  key="partial"
                  className="overflow-hidden"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <CpsAlert
                    type="info"
                    title="Nem todos os agendamentos têm disponibilidade"
                    description={partialDescriptionFor(
                      resolvedTargets.length,
                      targets.length,
                    )}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <div className="sticky bottom-0 bg-white pt-2 pb-2">
            <Button
              fullWidth
              loading={isPending}
              disabled={!canConfirm}
              onClick={handleConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/calendar/batch-adjust/review")({
  component: BatchAdjustReviewPage,
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    if (!context.flags["ajuste-em-lote-de-agendamentos"]) {
      redirect({ to: "/calendar", throw: true });
    }
    if (getDefaultStore().get(batchAdjustTargetsAtom).length === 0) {
      redirect({ to: "/calendar", throw: true });
    }
  },
});
